import { Link } from "react-router-dom";
import { REASON_TAGS, getCategoryColor } from "../lib/constants.js";

interface LeaderboardCardProps {
  startup: {
    id: string;
    slug: string;
    name: string;
    domain: string;
    logo_url: string | null;
    description: string | null;
    category: string;
    vibe_score: number;
    fragility_score: number;
    trusted_vote_count: number;
    trend_score: number;
    created_at?: string;
    top_tags?: { tag: string; count: number }[];
  };
  rank: number;
}

function vibeScoreDisplay(score: number): number {
  return Math.round(((score + 2) / 4) * 100);
}

function vibeColorClass(score: number): string {
  if (score >= 1) return "bg-emerald-500/15 text-emerald-400 border-emerald-500/25";
  if (score >= 0.3) return "bg-lime-500/15 text-lime-400 border-lime-500/25";
  if (score >= -0.3) return "bg-yellow-500/15 text-yellow-400 border-yellow-500/25";
  if (score >= -1) return "bg-orange-500/15 text-orange-400 border-orange-500/25";
  return "bg-red-500/15 text-red-400 border-red-500/25";
}

function categoryLabel(cat: string): string {
  return cat.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function isNew(createdAt?: string): boolean {
  if (!createdAt) return false;
  const weekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
  return new Date(createdAt).getTime() > weekAgo;
}

function getRankIndicator(rank: number): string {
  if (rank === 1) return "\u{1F947}";
  if (rank === 2) return "\u{1F948}";
  if (rank === 3) return "\u{1F949}";
  return "";
}

export default function LeaderboardCard({ startup, rank }: LeaderboardCardProps) {
  const vibeDisplay = vibeScoreDisplay(startup.vibe_score);
  const vibeClasses = vibeColorClass(startup.vibe_score);
  const rankEmoji = getRankIndicator(rank);
  const showNew = isNew(startup.created_at);
  const isTrending = startup.trend_score > 0;

  return (
    <Link
      to={`/startup/${startup.slug}`}
      className="card-hover block group animate-fade-in"
    >
      <div className="flex items-start gap-3 sm:gap-4">
        {/* Rank indicator */}
        <div className="w-8 flex items-center justify-center shrink-0 pt-1">
          {rankEmoji ? (
            <span className="text-xl">{rankEmoji}</span>
          ) : (
            <span className="w-2 h-2 rounded-full bg-slate-700 group-hover:bg-slate-600 transition-colors" />
          )}
        </div>

        {/* Logo */}
        <div className="w-12 h-12 rounded-xl bg-slate-800/80 flex items-center justify-center overflow-hidden shrink-0 border border-slate-700/30 group-hover:border-slate-600/50 transition-all">
          {startup.logo_url ? (
            <img
              src={startup.logo_url}
              alt={startup.name}
              className="w-full h-full object-cover"
            />
          ) : (
            <span className="text-lg font-bold text-slate-500 group-hover:text-slate-400 transition-colors">
              {startup.name.charAt(0)}
            </span>
          )}
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-semibold text-slate-100 group-hover:text-white truncate transition-colors">
              {startup.name}
            </h3>
            <span className="text-xs text-slate-600 hidden sm:inline">
              {startup.domain}
            </span>
            {isTrending && (
              <span className="badge-trending text-[10px]">
                {"📈"} Trending
              </span>
            )}
            {showNew && (
              <span className="badge-new text-[10px]">
                {"✨"} New
              </span>
            )}
          </div>

          {startup.description && (
            <p className="text-sm text-slate-400 truncate mt-0.5 group-hover:text-slate-300 transition-colors">
              {startup.description}
            </p>
          )}

          <div className="flex items-center gap-2 mt-2 flex-wrap">
            {/* Category tag */}
            <span
              className={`badge border text-[10px] ${getCategoryColor(startup.category)}`}
            >
              {categoryLabel(startup.category)}
            </span>

            {/* Top tags */}
            {startup.top_tags?.slice(0, 2).map((t) => (
              <span
                key={t.tag}
                className="badge bg-slate-800/60 text-slate-500 text-[10px]"
              >
                {REASON_TAGS.find((r) => r.value === t.tag)?.label || t.tag}
              </span>
            ))}

            {/* Vote count pill */}
            <span className="badge bg-slate-800/40 text-slate-500 text-[10px]">
              {startup.trusted_vote_count} vote{startup.trusted_vote_count !== 1 ? "s" : ""}
            </span>
          </div>
        </div>

        {/* Vibe Score */}
        <div className="shrink-0 flex flex-col items-end gap-1">
          <div
            className={`text-2xl sm:text-3xl font-extrabold px-3 py-1 rounded-xl border ${vibeClasses}`}
          >
            {vibeDisplay}
          </div>
        </div>
      </div>
    </Link>
  );
}
